import { VaccinationDataBySchool, VaccinationDataPoint } from "./types";

/**
 * Loads the vaccination trend data from the CSV file
 * @returns A promise that resolves to a VaccinationDataBySchool object
 */
export async function loadVaccinationTrends(): Promise<VaccinationDataBySchool> {
  // Use memoization to avoid loading the CSV multiple times
  if (loadVaccinationTrends.cachedData) {
    return loadVaccinationTrends.cachedData;
  }

  try {
    // Path for public file access
    const response = await fetch("./files/VaxApp-UTD-20250319.csv");
    if (!response.ok) {
      throw new Error(
        `Failed to load CSV data: ${response.status} ${response.statusText}`
      );
    }

    const csvText = await response.text();
    loadVaccinationTrends.cachedData = parseVaccinationTrendsData(csvText);
    return loadVaccinationTrends.cachedData;
  } catch (error) {
    console.error("Error loading vaccination trend data:", error);
    // Return empty object as fallback
    return {};
  }
}

// Add cachedData property to the function for memoization
loadVaccinationTrends.cachedData = null as VaccinationDataBySchool | null;

/**
 * Parses the CSV data into VaccinationDataBySchool format
 * @param csvText The raw CSV text data
 * @returns A VaccinationDataBySchool object
 */
function parseVaccinationTrendsData(csvText: string): VaccinationDataBySchool {
  // Split the CSV into lines
  const lines = csvText.split("\n");

  // First line contains headers with year ranges
  const headers = lines[0].split(",").map((header) => header.trim());

  // Initialize the result object
  const result: VaccinationDataBySchool = {};

  // Skip the header row
  const dataLines = lines.slice(1);

  // Process each line of the CSV (each school)
  for (let i = 0; i < dataLines.length; i++) {
    const line = dataLines[i];

    // Skip empty lines
    if (!line.trim()) continue;

    // Split the line by comma
    const columns = line.split(",");

    // Extract school code (first column)
    const schoolCode = columns[0];

    // Skip if no school code
    if (!schoolCode) continue;

    // Initialize array for this school
    result[schoolCode] = [];

    // Process each year (starting from index 1, as 0 is the school code)
    for (let j = 1; j < headers.length; j++) {
      const year = headers[j];
      const percentageStr = columns[j];

      // Skip if no percentage data for this year
      if (!percentageStr || percentageStr === "" || percentageStr === ".")
        continue;

      // Parse percentage value
      const percentage = parseFloat(percentageStr);

      // Skip if NaN
      if (isNaN(percentage)) continue;

      // Add data point
      result[schoolCode].push({
        year: year,
        percentage: percentage,
      });
    }

    // Remove schools with no data points
    if (result[schoolCode].length === 0) {
      delete result[schoolCode];
    }
  }

  return result;
}
